import { useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Download, FileText, RefreshCw, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import { useAuthedFileUrl, type AuthedFileUrl } from '@/lib/api/authed-file';
import {
  usePdfReportQuery,
  useRequestPdfRenderMutation,
  type PdfReportDetail,
} from '@/lib/api/snapshots';
import { formatDateTime } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * T-0115 / E10 — PDF report preview modal (T-0059 / T-0089 / ADR-009).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-round3.js`
 * + `modals-round4b.js` PDF-row triggers, `README.md` §4 modal
 * `data-open-pdf`, §8 pattern 4 (modal with embedded preview +
 * download fallback for screen-reader users).
 *
 * What this modal does:
 *   - Loads the latest pdf_reports row for a snapshot via GET
 *     `/api/v1/engagements/:id/reports/:snapshot_id/pdf`.
 *   - Shows the render status (succeeded / rendering / failed) + the
 *     brand-template version + the rendered_at timestamp.
 *   - Embeds the PDF inline via browser-native `<object>` when the
 *     render has succeeded and a download_url is available. Falls back
 *     to a prominent "Download PDF" button when the embed isn't
 *     supported (e.g. screen-reader-driven a11y path per ticket §6).
 *     download_url is an API path behind sign-in (the blob is private),
 *     so the bytes are fetched with the bearer token and both the embed
 *     and the download link use the resulting object URL.
 *   - "Regenerate PDF" affordance kicks a fresh render — per ADR-009 a
 *     re-render creates a NEW pdf_reports row; the existing row stays
 *     frozen (write-once contract).
 *   - Polls the report every 2s while `render_status === 'rendering' ||
 *     'enqueued'`; auto-stops on terminal state.
 *
 * Accessibility: Esc closes, focus-trap via Radix Dialog. The download
 * link is always present so screen-reader users have a non-embed path.
 */

const TERMINAL_STATUSES = new Set(['succeeded', 'failed']);

export interface PdfPreviewModalProps {
  engagementId: string;
  snapshotId: string;
  /** Optional caption: snapshot type / sequence_no. */
  snapshotLabel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PdfPreviewModal({
  engagementId,
  snapshotId,
  snapshotLabel,
  open,
  onOpenChange,
}: PdfPreviewModalProps) {
  // Pre-resolve a query so we know whether the report exists. If the
  // first GET 404s, we surface a "Generate PDF" affordance — the
  // snapshot may not have been rendered yet.
  const reportQuery = usePdfReportQuery(open ? engagementId : undefined, snapshotId, {
    enabled: open,
  });

  // Determine the report's terminal/rendering state for polling.
  const isRendering = useMemo(() => {
    const status = reportQuery.data?.render_status;
    if (!status) return false;
    return !TERMINAL_STATUSES.has(status);
  }, [reportQuery.data?.render_status]);

  // Switch the query to a polling cadence while the render is in flight.
  // useQuery accepts a refetchInterval at the hook level — we re-subscribe
  // with the appropriate interval whenever `isRendering` flips.
  const pollingQuery = usePdfReportQuery(open ? engagementId : undefined, snapshotId, {
    enabled: open && isRendering,
    pollMs: 2000,
  });
  // Prefer the polling query's data while it's in flight; otherwise the
  // base query is the source of truth.
  const report: PdfReportDetail | undefined = pollingQuery.data ?? reportQuery.data;
  const error = pollingQuery.error ?? reportQuery.error;
  const isLoading = reportQuery.isLoading && !report;

  const renderMutation = useRequestPdfRenderMutation(engagementId);

  // The rendered bytes, fetched with the session's credentials.
  const pdfFile = useAuthedFileUrl(
    report?.render_status === 'succeeded' ? report.download_url : null,
    'application/pdf',
  );

  // 404 means the snapshot doesn't have a pdf_reports row yet — render
  // a "Generate PDF" CTA rather than a loud error.
  const noReportYet = error instanceof ApiError && error.status === 404;

  // Reset state on close.
  useEffect(() => {
    if (!open) {
      renderMutation.reset();
    }
  }, [open, renderMutation]);

  async function regenerate() {
    if (renderMutation.isPending) return;
    const idempotencyKey =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `pdf-render-${Date.now()}`;
    await renderMutation.mutateAsync({ snapshotId, idempotencyKey });
    // The mutation kicks a new render — invalidate the query so the
    // polling cycle picks up the new row.
    await reportQuery.refetch();
  }

  const titleId = `pdf-preview-modal-title-${snapshotId}`;
  const status = report?.render_status ?? (noReportYet ? 'not_rendered' : 'unknown');

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1100px,95vw)] max-h-[92vh] min-h-[60vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0 flex items-start gap-3">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-card border border-ink-200 bg-paper-50 text-ink-500"
              >
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-meta uppercase tracking-wide text-ink-500">
                  PDF report · fixed once rendered
                </div>
                <Dialog.Title
                  id={titleId}
                  className="mt-1 truncate text-h2 font-semibold text-ink-900"
                >
                  {snapshotLabel ?? 'Snapshot PDF'}
                </Dialog.Title>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-meta text-ink-500">
                  <RenderStatusBadge status={status} />
                  {report?.brand_template_version ? (
                    <span>
                      brand <span className="font-mono">{report.brand_template_version}</span>
                    </span>
                  ) : null}
                  {report?.rendered_at ? (
                    <span>rendered {formatDateTime(report.rendered_at)}</span>
                  ) : null}
                  {report?.size_bytes ? <span>{formatBytes(report.size_bytes)}</span> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden bg-paper-50">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-body-sm text-ink-500">
                <Spinner />
                <span>Loading PDF report…</span>
              </div>
            ) : noReportYet ? (
              <NoReportYet onGenerate={regenerate} pending={renderMutation.isPending} />
            ) : error ? (
              <div className="p-6">
                <ErrorMessage error={error} />
              </div>
            ) : report ? (
              <PdfBody report={report} file={pdfFile} />
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 bg-paper-0 px-6 py-3">
            <div className="text-meta text-ink-500">
              {report?.render_status === 'succeeded'
                ? 'Rendering again creates a new PDF; this one stays as it is.'
                : 'Rendering…'}
            </div>
            <div className="flex items-center gap-2">
              {pdfFile.url ? (
                <a
                  href={pdfFile.url}
                  download={pdfFileName(snapshotLabel)}
                  className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-900',
                    'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  )}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Download PDF</span>
                </a>
              ) : null}
              {report?.render_status === 'succeeded' || report?.render_status === 'failed' ? (
                <button
                  type="button"
                  onClick={regenerate}
                  disabled={renderMutation.isPending}
                  className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                    'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {renderMutation.isPending ? (
                    <Spinner />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  <span>Regenerate PDF</span>
                </button>
              ) : null}
              <Dialog.Close
                className={cn(
                  'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                Close
              </Dialog.Close>
            </div>
          </div>

          <Dialog.Close
            aria-label="Close PDF preview"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PdfBody({ report, file }: { report: PdfReportDetail; file: AuthedFileUrl }) {
  const status = report.render_status;
  if (status === 'succeeded') {
    if (report.download_url && file.failed) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-center text-body-sm text-ink-500">
          <FileText className="h-8 w-8 text-ink-300" aria-hidden="true" />
          <p>The PDF could not be loaded. Close this window and open it again.</p>
        </div>
      );
    }
    if (report.download_url && !file.url) {
      return (
        <div className="flex items-center justify-center gap-2 py-16 text-body-sm text-ink-500">
          <Spinner />
          <span>Loading PDF…</span>
        </div>
      );
    }
    return file.url ? (
      // Browser-native PDF embed. Per the ticket §6 risk, screen readers
      // don't reliably narrate `<embed>` — the download anchor in the
      // footer is the a11y fallback.
      <object
        data={file.url}
        type="application/pdf"
        className="block h-[68vh] w-full"
        aria-label="Rendered PDF preview"
      >
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-center text-body-sm text-ink-500">
          <FileText className="h-8 w-8 text-ink-300" aria-hidden="true" />
          <p>
            Your browser can&apos;t display PDFs inline. Use the download link in the footer to open
            the file locally.
          </p>
        </div>
      </object>
    ) : (
      <div className="flex flex-col items-center justify-center gap-3 p-12 text-center text-body-sm text-ink-500">
        <FileText className="h-8 w-8 text-ink-300" aria-hidden="true" />
        <p>The render succeeded but the download URL hasn&apos;t materialized yet — refresh.</p>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-body-sm text-posture-critical">
        <p className="font-semibold">Render failed.</p>
        {report.render_error ? (
          <p className="max-w-md font-mono text-meta text-ink-700">{report.render_error}</p>
        ) : null}
        <p className="max-w-md text-ink-500">
          Use the regenerate button to kick a new render — the failed row stays in the audit trail.
        </p>
      </div>
    );
  }
  // rendering / enqueued / unknown — render the polling state.
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-16 text-center text-body-sm text-ink-500">
      <Spinner />
      <p>The PDF is rendering. This usually takes under 60 seconds.</p>
    </div>
  );
}

function NoReportYet({ onGenerate, pending }: { onGenerate: () => void; pending: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-16 text-center text-body-sm text-ink-500">
      <FileText className="h-8 w-8 text-ink-300" aria-hidden="true" />
      <p className="max-w-md">No PDF has been rendered for this snapshot yet. Render the PDF.</p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={pending}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
          'hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        {pending ? <Spinner /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
        <span>Generate PDF</span>
      </button>
    </div>
  );
}

function RenderStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'succeeded'
      ? 'bg-posture-healthy-tint text-posture-healthy'
      : status === 'failed'
        ? 'bg-posture-critical-tint text-posture-critical'
        : 'bg-ai-surface text-ai-ink';
  const label =
    status === 'succeeded'
      ? 'Sealed'
      : status === 'failed'
        ? 'Failed'
        : status === 'not_rendered'
          ? 'Not rendered yet'
          : 'Rendering…';
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center rounded-full px-2.5 text-meta font-medium',
        tone,
      )}
    >
      {label}
    </span>
  );
}

/** The saved file's name: the modal's caption, made safe for a file system. */
function pdfFileName(label: string | undefined): string {
  const base = (label ?? '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${base || 'snapshot-report'}.pdf`;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let idx = 0;
  let val = n;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  return `${val.toFixed(val >= 100 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}
